import React from 'react';
import MultiSelectDropdown from '../../ui/MultiSelectDropdown';
import SearchableDropdown from '../../ui/SearchableDropdown';
import { UseMapVisualizationReturn } from '../../../hooks/useMapVisualization';

interface MapFiltersPanelProps {
  mapVisualization: UseMapVisualizationReturn;
  availablePeriods: Array<{ id: string; name: string }>;
  availableTags: string[];
  loadingData?: boolean;
  compareMode?: boolean;
  showChips?: boolean;
  showOnlyMatchPeriod?: boolean; // Show only match period filter (e.g., for Tactics tab)
  disableFiltersForTeamShape?: boolean; // Disable all filters except Team and Match Period when team_shape is selected
}

/**
 * MapFiltersPanel - Reusable filter panel component for map visualizations
 * Extracted from MapsTab to be reusable across components
 */
const MapFiltersPanel: React.FC<MapFiltersPanelProps> = ({
  mapVisualization,
  availablePeriods,
  availableTags,
  loadingData = false,
  compareMode = false,
  showChips = true,
  showOnlyMatchPeriod = false,
  disableFiltersForTeamShape = false,
}) => {
  const {
    filters,
    handleFilterChange,
    handleClearFilters,
    hasActiveFilters,
    statTypeOptions,
    playerOptions,
    tagOptions,
    teamOptions,
    selectedStatTypes,
    setSelectedStatTypes,
    selectedTags,
    setSelectedTags,
  } = mapVisualization;

  // Memoize selectedIds arrays to prevent unnecessary re-renders
  const statTypeSelectedIds = filters.stat_type_id || [];
  const tagsSelectedIds = filters.tags || [];

  return (
    <>
      {/* Advanced Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-blue-50 rounded flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-900">Filters</h3>
          </div>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        <div className={showOnlyMatchPeriod ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"}>
          {!showOnlyMatchPeriod && (
            <>
              {/* Stat Type Multi-Select */}
              <div>
                <label className={`block text-xs font-medium mb-1 ${disableFiltersForTeamShape ? 'text-gray-400' : 'text-gray-600'}`}>
                  Stat Types
                </label>
                <MultiSelectDropdown
                  selectedIds={statTypeSelectedIds}
                  onChange={(ids) => handleFilterChange('stat_type_id', ids)}
                  onSelectedOptionsChange={setSelectedStatTypes}
                  options={statTypeOptions}
                  placeholder="Select stat types..."
                  disabled={loadingData || disableFiltersForTeamShape}
                />
              </div>

              {/* Team Selector */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Team
                </label>
                <MultiSelectDropdown
                  selectedIds={filters.team_id || []}
                  onChange={(ids) => handleFilterChange('team_id', ids)}
                  options={teamOptions}
                  placeholder="Select teams..."
                  disabled={compareMode}
                />
              </div>

              {/* Player Selector */}
              <div>
                <label className={`block text-xs font-medium mb-1 ${disableFiltersForTeamShape ? 'text-gray-400' : 'text-gray-600'}`}>
                  Player
                </label>
                <SearchableDropdown
                  value={filters.player_id || ''}
                  onChange={(value) => handleFilterChange('player_id', value || undefined)}
                  options={playerOptions}
                  placeholder="Select player..."
                  disabled={compareMode || disableFiltersForTeamShape}
                />
              </div>

              {/* Tags Multi-Select */}
              <div>
                <label className={`block text-xs font-medium mb-1 ${disableFiltersForTeamShape ? 'text-gray-400' : 'text-gray-600'}`}>
                  Tags
                </label>
                <MultiSelectDropdown
                  selectedIds={tagsSelectedIds}
                  onChange={(ids) => handleFilterChange('tags', ids)}
                  onSelectedOptionsChange={setSelectedTags}
                  options={tagOptions}
                  placeholder="Select tags..."
                  disabled={availableTags.length === 0 || disableFiltersForTeamShape}
                />
              </div>
            </>
          )}

          {/* Match Period Selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Match Period
            </label>
            <SearchableDropdown
              value={filters.match_period || ''}
              onChange={(value) => handleFilterChange('match_period', value || undefined)}
              options={[
                { id: '', name: 'All Periods' },
                ...availablePeriods.map((period) => ({
                  id: period.id,
                  name: period.name,
                })),
              ]}
              placeholder="Select match period..."
              disabled={availablePeriods.length === 0}
            />
          </div>
        </div>
      </div>

      {/* Selected Filters Chips - Display above pitch */}
      {showChips && (
        (!showOnlyMatchPeriod && (selectedStatTypes.length > 0 || selectedTags.length > 0 || 
          filters.player_id)) || 
        filters.match_period
      ) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
          <div className="flex items-center space-x-1.5 mb-1.5">
            <span className="text-xs font-medium text-gray-600">Active Filters:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {/* Stat Type Chips */}
            {!showOnlyMatchPeriod && selectedStatTypes.map((statType) => (
              <span
                key={statType.id}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-normal text-blue-700 border border-blue-300 bg-transparent hover:bg-blue-50"
              >
                {statType.name}
                <button
                  type="button"
                  onClick={() => {
                    handleFilterChange('stat_type_id', (filters.stat_type_id || []).filter(id => id !== statType.id));
                  }}
                  className="ml-1 inline-flex items-center justify-center w-3 h-3 rounded hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            ))}
            
            {/* Tag Chips */}
            {!showOnlyMatchPeriod && selectedTags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-normal text-purple-700 border border-purple-300 bg-transparent hover:bg-purple-50"
              >
                {tag.name}
                <button
                  type="button"
                  onClick={() => {
                    handleFilterChange('tags', (filters.tags || []).filter(id => id !== tag.id));
                  }}
                  className="ml-1 inline-flex items-center justify-center w-3 h-3 rounded hover:bg-purple-100 transition-colors"
                >
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            ))}
            
            {/* Player Chip */}
            {!showOnlyMatchPeriod && filters.player_id && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-normal text-orange-700 border border-orange-300 bg-transparent hover:bg-orange-50">
                Player: {playerOptions.find(p => p.id === filters.player_id)?.name}
                <button
                  type="button"
                  onClick={() => handleFilterChange('player_id', undefined)}
                  className="ml-1 inline-flex items-center justify-center w-3 h-3 rounded hover:bg-orange-100 transition-colors"
                >
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            )}
            
            {/* Match Period Chip */}
            {filters.match_period && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-normal text-indigo-700 border border-indigo-300 bg-transparent hover:bg-indigo-50">
                Period: {availablePeriods.find(p => p.id === filters.match_period)?.name || filters.match_period}
                <button
                  type="button"
                  onClick={() => handleFilterChange('match_period', undefined)}
                  className="ml-1 inline-flex items-center justify-center w-3 h-3 rounded hover:bg-indigo-100 transition-colors"
                >
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MapFiltersPanel;

